import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../use-is-mounted";
import Api from "../../../core/services/api/api";
import CacheService from "../../../core/services/cache/cache_service";
import LocalStorageService from "../../../core/services/cache/local_storage_service";
import {useAuthenticationState} from "../use-auth-state";
import EnvService from "../../../core/services/env";
import {generateBusinessName} from "../../../core/services/utils/name-generator";


const CachedPriceLevelDictionaryControllerContext = createContext({
    getPriceLevelDictionary: async () => void 0,
    savePriceLevelDictionary: async () => void 0,
    invalidateDictionaryCache: () => void 0,
    priceLevelDict: [],
});

export const CachedPriceLevelsDictionaryProvider = ({children}) => {
    const [cache, setCache] = useState(CacheService.getPriceLevelDictionary());
    const isMounted = useIsMounted();
    const cacheRef = useRef(cache);
    const {loggedIn} = useAuthenticationState();

    /**
     * As soon as the component is mounted:
     * - Listen to the storage event to update the cache.
     */
    useEffect(() => {
        const storageListener = (e) => {
            if (e.key === LocalStorageService.keys.priceLevelDictionary) {
                if (!e.newValue) {
                    cacheRef.current = undefined;
                    return setCache([]);
                }
                setCache(prevState => {
                    const newState = [...(prevState ?? []), ...(JSON.parse(e.newValue) ?? [])];
                    cacheRef.current = newState;
                    return newState
                });
            }
        }
        window.addEventListener('storage', storageListener);
        return () => window.removeEventListener('storage', storageListener);
    }, [isMounted])


    /**
     * Fetches the property groups of an industry.
     * @type {(function(): Promise<null|*|undefined>)}
     */
    const getPriceLevelDictionary = useCallback(async () => {
        if (!loggedIn) {
            return null; // not logged in
        }
        if (cacheRef.current) {
            return cacheRef.current;
        }
        const response = await Api.listPriceLevelTitleDictionary();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCache(prevState => {
                let newState = [...(prevState ?? []), ...(response?.data ?? [])];
                if (EnvService.enableDemoNameMasking) {
                    newState = newState.map(item => ({
                        ...item,
                        title: generateBusinessName(item.title)
                    }))
                }
                CacheService.setPriceLevelDictionary(newState)
                cacheRef.current = newState;
                return newState
            })
        }
        return response?.data ?? [];
    }, [isMounted, loggedIn])

    /**
     * Saves the property groups of an industry.
     *
     * @type {(function(*, *): Promise<*>)}
     */
    const savePriceLevelDictionary = useCallback(async (priceLevelId, title) => {
        if (!loggedIn) {
            return null; // not logged in
        }
        const response = await Api.savePriceLevelTitleDictionary({
            priceLevelId: priceLevelId,
            title: title
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCache(prevState => {
                const list = prevState ?? [];
                const found = list.find(item => item.priceLevelId === priceLevelId);
                const newState = found ? list.map(item => item.priceLevelId === priceLevelId ? {
                    ...item,
                    title: title
                } : item) : [...list, response.data];
                CacheService.setPriceLevelDictionary(newState)
                cacheRef.current = newState;
                return newState
            })
        }
        return response;
    }, [isMounted, loggedIn])

    /**
     * Invalidates the cache for the property groups of an industry or all industries if no industry id is provided.
     * @type {(function(*): void)}
     */
    const invalidateDictionaryCache = useCallback(() => {
        setCache(() => {
            CacheService.setPriceLevelDictionary(undefined)
            cacheRef.current = undefined;
            return [];
        });
    }, [])

    const controller = useMemo(() => ({
        getPriceLevelDictionary: getPriceLevelDictionary,
        invalidateDictionaryCache: invalidateDictionaryCache,
        savePriceLevelDictionary: savePriceLevelDictionary,
        priceLevelDict: cache,
    }), [getPriceLevelDictionary, invalidateDictionaryCache, savePriceLevelDictionary, cache])

    return (
        <>
            <CachedPriceLevelDictionaryControllerContext.Provider value={controller}>
                {children}
            </CachedPriceLevelDictionaryControllerContext.Provider>
        </>
    );
}

/**
 * Returns the cached property groups controller
 */
export const useCachedPriceLevelsDictionaryController = ({loadDict = false} = {}) => {
    const controller = useContext(CachedPriceLevelDictionaryControllerContext);
    const {getPriceLevelDictionary} = controller;

    /**
     * Fetch the property groups of an industry if the loadDict flag is set to true.
     * - This is useful when the dictionary is required immediately after the component is mounted.
     */
    useEffect(() => {
        if (loadDict) {
            void getPriceLevelDictionary();
        }
    }, [loadDict, getPriceLevelDictionary])

    return controller;
}

export default useCachedPriceLevelsDictionaryController;
